import isEmpty from 'lodash/isEmpty';
import { ProductImages } from './product_images_model';
import { getSquareImage } from './utils';
import { ProductImageGridView } from './product_image_grid_view';
import { ProductImageSlideView } from './product_image_slide_view';
import { ProductImageUploadView } from './product_image_upload_view';

/** ProductImageView - provides the look-and-feel for managing images in a product item view */

export class ProductImageView implements MerchantosProductImageView {
  private productImages?: ProductImages;

  private id: string | number | undefined;

  private $imageContainer = $('div.image');

  private $uploadContainer?: JQuery<HTMLElement>;

  private $displayContainer?: JQuery<HTMLElement>;

  private $uploadCollapsedView?: JQuery<HTMLElement>;

  private imageUpload?: ProductImageUploadView;

  private gridView?: ProductImageGridView;

  private slideView?: ProductImageSlideView | null;

  private type = 'Item';

  private imageType: ImageType = 'Image';

  private productMatrix = false;

  private readonly = false;

  constructor(id: string | undefined) {
    this.id = id;
    if (this.$imageContainer.length > 0) {
      if (this.id) {
        if (this.$imageContainer.hasClass('matrix')) {
          this.type = 'ItemMatrix';
          this.productMatrix = true;
        } else if (this.$imageContainer.hasClass('workorder')) {
          this.type = 'Workorder';
          this.imageType = 'WorkorderImage';
        }

        if (this.$imageContainer.hasClass('readonly')) {
          this.readonly = true;
        }
        this.$uploadCollapsedView = this.$imageContainer.find('div.prompt').first();
        this.$uploadContainer = this.$imageContainer.find('div.upload').first();
        this.$displayContainer = this.$imageContainer.find('div.image-display').first();
        this.reload();
      } else {
        // the image API requires an id so hide the image
        this.$imageContainer.hide();
      }
    }
  }

  reload: MerchantosProductImageView['reload'] = forceReload => {
    this.initProductImages(forceReload).then(this.render);
  };

  initProductImages: MerchantosProductImageView['initProductImages'] = forceReload => {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | number | undefined' is ... Remove this comment to see the full error message
    this.productImages = new ProductImages(this.id, {
      type: this.type,
      imageType: this.imageType,
    });

    return this.productImages.fetchProductImages(forceReload);
  };

  render: MerchantosProductImageView['render'] = () => {
    if (this.productImages && this.productImages.images.listSize > 0) {
      this.showImageView();
    } else {
      this.showUploadView();
    }
  };

  showUploadView: MerchantosProductImageView['showUploadView'] = () => {
    if (
      this.$uploadCollapsedView &&
      this.$displayContainer &&
      this.$uploadCollapsedView.length > 0 &&
      this.$displayContainer.length > 0 &&
      !this.readonly
    ) {
      if (this.imageUpload instanceof ProductImageUploadView) {
        this.imageUpload.disable();
        delete this.imageUpload;
      }
      this.imageUpload = new ProductImageUploadView(this.$imageContainer, {
        type: this.type,
        imageType: this.imageType,
        productMatrix: this.productMatrix,
      });
      this.$imageContainer.on('alluploadscomplete', () => {
        this.imageUpload?.disable();
        this.reload(true);
      });

      this.imageUpload.collapse();
      this.$displayContainer.hide();
    }
  };

  showImageView: MerchantosProductImageView['showImageView'] = () => {
    // Check if the DOM has a upload container (omitted for readonly) and if
    // the upload area is hidden.
    if (
      (!this.$uploadContainer?.length || !isEmpty(this.$uploadCollapsedView)) &&
      !isEmpty(this.$displayContainer) &&
      this.$displayContainer &&
      this.productImages
    ) {
      this.$uploadCollapsedView?.addClass('hide');
      this.$uploadContainer?.addClass('hide');

      const $imageElement = this.$displayContainer.find<HTMLImageElement>(
        '.image-display-featured'
      );

      getSquareImage($imageElement, this.productImages.images.head);
      this.$displayContainer.find('.image-display-count').text(this.productImages.images.listSize);

      this.$imageContainer
        .find('.image-display-manage')
        .off('click')
        .on('click', () => this.showGridView());

      this.$imageContainer
        .find('.image-display-featured:not(.image-display-manage)')
        .off('click')
        .on('click', () => this.showSlideView());

      this.$displayContainer.show();
    }
  };

  showGridView: MerchantosProductImageView['showGridView'] = () => {
    // need to refresh the product images in case it has become out of sync with the back-end
    this.initProductImages().then(() => {
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | number | undefined' is ... Remove this comment to see the full error message
      this.gridView = new ProductImageGridView(this.id, this.productImages!, {
        type: this.type,
        imageType: this.imageType,
        productMatrix: this.productMatrix,
        readonly: this.readonly,
      });

      this.gridView.$gridView.on('closed', () => {
        this.gridView = undefined;
        if (!this.productImages || !this.$displayContainer) {
          return;
        }
        const $imageElement = this.$displayContainer.find<HTMLImageElement>(
          '.image-display-featured'
        );
        getSquareImage($imageElement, this.productImages.images.head);
        this.$displayContainer
          ?.find('.image-display-count')
          .text(this.productImages.images.listSize);

        if (!this.readonly) {
          this.productImages?.updateOrder();
        }
      });
      this.gridView.$gridView.on('imageselected', (_event, imageId) => {
        this.gridView = undefined;
        if (!this.readonly) {
          this.productImages?.updateOrder();
        }
        this.showSlideView(imageId);
      });
    });
  };

  showSlideView: MerchantosProductImageView['showSlideView'] = imageId => {
    // need to refresh the product images in case it has become out of sync with the back-end
    this.initProductImages().then(() => {
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | number | undefined' is ... Remove this comment to see the full error message
      this.slideView = new ProductImageSlideView(this.id, this.productImages!, imageId, {
        type: this.type,
        imageType: this.imageType,
        readonly: this.readonly,
      });

      this.slideView.$slideView.on('closed', () => {
        this.slideView = null;

        if (!this.readonly) {
          this.productImages?.updateOrder();
        }
        this.reload();
      });
      this.slideView.$slideView.on('gridselected', () => {
        this.slideView = null;

        if (!this.readonly) {
          this.productImages?.updateOrder();
        }
        this.showGridView();
      });
    });
  };
}
